import React, { createContext, useContext, useEffect, useState } from "react";
import { INTRO_ANIMATION_MS } from "../styles/tokens";

/**
 * IntroContext — single source of truth for whether the first-visit
 * logo intro has finished playing.
 *
 * Why this exists:
 *   Reading `sessionStorage` inside a component's `useState` initializer
 *   is unsafe during Gatsby SSR → client hydration. The server can't
 *   read sessionStorage, so SSR returns one value (treating intro as
 *   "done") while the client's first-visit render returns another
 *   (treating intro as "pending"). The class-name mismatch causes the
 *   hero's reveal animation to start under the intro overlay, finish
 *   before the overlay lifts, and leave the hero frozen in its final
 *   state with no visible animation.
 *
 * How this works:
 *   Both SSR and client render with `introDone = false`. After mount,
 *   the effect checks sessionStorage. Returning visitors flip the flag
 *   synchronously in the effect; first-time visitors flip it after the
 *   intro timer. Consumers (Layout for the overlay, index for the hero
 *   reveals) key their behavior off `introDone`, so there is no race.
 */

const IntroContext = createContext({ introDone: false });

export const IntroProvider = ({ children }) => {
  // Identical initial state on server and client → no hydration mismatch.
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Returning visitor (same session): skip the intro instantly.
    if (sessionStorage.getItem("animationPlayed") === "true") {
      setIntroDone(true);
      return;
    }

    // First visit: record the play and flip the flag when the logo
    // animation would finish. INTRO_ANIMATION_MS must match the
    // `fadeInOut` duration in LogoAnimation.js.
    sessionStorage.setItem("animationPlayed", "true");
    const t = setTimeout(() => setIntroDone(true), INTRO_ANIMATION_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <IntroContext.Provider value={{ introDone }}>
      {children}
    </IntroContext.Provider>
  );
};

export const useIntro = () => useContext(IntroContext);
